if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
      this.prependMedia = this.dataset.disablePrepend != 'true';
      if (this.productInfo && Shopify.postLinksRetry) this.productInfo.initShareLinks();
      this.filteringOption = this.dataset.filteringOption;
      this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');
      this.updateMobileGalleryHeight = debounce(this.setMobileGalleryHeight.bind(this), 100);
      this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
      if (this.elements.viewer.slider) {
        this.elements.viewer.slider.addEventListener('scroll', this.onMobileGalleryScroll.bind(this), { passive: true });
      }
      this.elements.viewer.querySelectorAll('.slider-button--prev, .splide__arrow--prev').forEach((button) => {
        button.addEventListener('click', this.onMobileArrowClick.bind(this, -1), { capture: true });
      });
      this.elements.viewer.querySelectorAll('.slider-button--next, .splide__arrow--next').forEach((button) => {
        button.addEventListener('click', this.onMobileArrowClick.bind(this, 1), { capture: true });
      });
      this.elements.viewer.querySelectorAll('.product__media-item .product__modal-opener--image').forEach((opener) => {
        opener.addEventListener('click', this.onMobileImageZoom.bind(this), { capture: true });
      });
      this.mobileMediaQuery.addEventListener('change', this.updateMobileGalleryHeight);
      window.addEventListener('resize', this.updateMobileGalleryHeight);
      this.querySelectorAll('.product__media-item img').forEach((image) => {
        if (image.complete) return;
        image.addEventListener('load', this.updateMobileGalleryHeight, { once: true });
      });
      requestAnimationFrame(() => this.setMobileGalleryHeight());
      if (!this.elements.thumbnails) return;

      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    onSlideChanged(event) {
      if (this.mobileMediaQuery.matches) {
        this.elements.viewer.querySelectorAll('.product__media-item').forEach((item) => {
          item.classList.toggle('is-active', item === event.detail.currentElement);
        });
      }
      this.setMobileGalleryHeight(event.detail.currentElement);
      if (!this.elements.thumbnails) return;
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    onMobileGalleryScroll() {
      if (!this.mobileMediaQuery.matches || this.mobileScrollFrame) return;
      this.mobileScrollFrame = requestAnimationFrame(() => {
        this.mobileScrollFrame = null;
        const slider = this.elements.viewer.slider;
        const sliderCenter = slider.getBoundingClientRect().left + slider.clientWidth / 2;
        const slides = Array.from(slider.querySelectorAll('.product__media-item:not(.hidden)'));
        const currentSlide = slides.reduce((closest, slide) => {
          const rect = slide.getBoundingClientRect();
          const distance = Math.abs(rect.left + rect.width / 2 - sliderCenter);
          return !closest || distance < closest.distance ? { slide, distance } : closest;
        }, null);
        if (!currentSlide) return;
        slides.forEach((slide) => slide.classList.toggle('is-active', slide === currentSlide.slide));
        this.setMobileGalleryHeight(currentSlide.slide);
      });
    }

    onMobileArrowClick(direction, event) {
      if (!this.mobileMediaQuery.matches) return;
      const slider = this.elements.viewer.slider;
      if (!slider) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const slides = Array.from(slider.querySelectorAll('.product__media-item:not(.hidden)'));
      const sliderCenter = slider.getBoundingClientRect().left + slider.clientWidth / 2;
      const currentIndex = slides.reduce((closestIndex, slide, index) => {
        const currentRect = slides[closestIndex].getBoundingClientRect();
        const slideRect = slide.getBoundingClientRect();
        const currentDistance = Math.abs(currentRect.left + currentRect.width / 2 - sliderCenter);
        const slideDistance = Math.abs(slideRect.left + slideRect.width / 2 - sliderCenter);
        return slideDistance < currentDistance ? index : closestIndex;
      }, 0);
      const targetIndex = Math.min(Math.max(currentIndex + direction, 0), slides.length - 1);
      const targetSlide = slides[targetIndex];
      if (!targetSlide || targetIndex === currentIndex) return;
      slides.forEach((slide) => slide.classList.toggle('is-active', slide === targetSlide));
      this.setMobileGalleryHeight(targetSlide);
      const scrollBehavior = slider.style.scrollBehavior;
      slider.style.scrollBehavior = 'auto';
      slider.scrollLeft = targetSlide.offsetLeft;
      requestAnimationFrame(() => {
        slider.style.scrollBehavior = scrollBehavior;
      });
    }

    onMobileImageZoom(event) {
      if (!this.mobileMediaQuery.matches) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const slide = event.currentTarget.closest('.product__media-item');
      const imageSlides = Array.from(this.elements.viewer.slider.querySelectorAll('.product__media-item:not(.hidden)')).filter((item) => item.querySelector('.product__modal-opener--image img'));
      const imageIndex = imageSlides.indexOf(slide);
      if (imageIndex < 0) return;
      this.mobileLightboxSlides = imageSlides;
      this.mobileLightboxIndex = imageIndex;
      this.openMobileLightbox();
    }

    openMobileLightbox() {
      if (!this.mobileLightbox) this.createMobileLightbox();
      this.mobileLightbox.classList.add('is-open');
      this.mobileLightbox.setAttribute('aria-hidden', 'false');
      this.mobileLightboxPreviousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.renderMobileLightboxImage();
      this.mobileLightbox.querySelector('.mobile-media-lightbox__close').focus();
    }

    createMobileLightbox() {
      this.mobileLightbox = document.createElement('div');
      this.mobileLightbox.className = 'mobile-media-lightbox';
      this.mobileLightbox.setAttribute('role', 'dialog');
      this.mobileLightbox.setAttribute('aria-modal', 'true');
      this.mobileLightbox.setAttribute('aria-hidden', 'true');
      this.mobileLightbox.innerHTML = `
        <div class="mobile-media-lightbox__toolbar">
          <button type="button" class="mobile-media-lightbox__zoom" aria-label="Zoom image">
            <svg aria-hidden="true" viewBox="0 0 24 24"><circle cx="10.5" cy="10.5" r="6.5"></circle><path d="M15.5 15.5 21 21M10.5 7v7M7 10.5h7"></path></svg>
          </button>
          <button type="button" class="mobile-media-lightbox__close" aria-label="Close image zoom">
            <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M5 5l14 14M19 5 5 19"></path></svg>
          </button>
        </div>
        <button type="button" class="mobile-media-lightbox__arrow mobile-media-lightbox__arrow--prev" aria-label="Previous image">
          <svg aria-hidden="true" viewBox="0 0 24 24"><path d="m15 4-8 8 8 8"></path></svg>
        </button>
        <div class="mobile-media-lightbox__stage">
          <img class="mobile-media-lightbox__image" alt="">
        </div>
        <button type="button" class="mobile-media-lightbox__arrow mobile-media-lightbox__arrow--next" aria-label="Next image">
          <svg aria-hidden="true" viewBox="0 0 24 24"><path d="m9 4 8 8-8 8"></path></svg>
        </button>`;
      document.body.appendChild(this.mobileLightbox);
      this.mobileLightbox.querySelector('.mobile-media-lightbox__close').addEventListener('click', this.closeMobileLightbox.bind(this));
      this.mobileLightbox.querySelector('.mobile-media-lightbox__zoom').addEventListener('click', this.toggleMobileLightboxZoom.bind(this));
      this.mobileLightbox.querySelector('.mobile-media-lightbox__image').addEventListener('click', this.toggleMobileLightboxZoom.bind(this));
      this.mobileLightbox.querySelector('.mobile-media-lightbox__arrow--prev').addEventListener('click', this.changeMobileLightboxImage.bind(this, -1));
      this.mobileLightbox.querySelector('.mobile-media-lightbox__arrow--next').addEventListener('click', this.changeMobileLightboxImage.bind(this, 1));
      this.mobileLightbox.addEventListener('click', (event) => {
        if (event.target === this.mobileLightbox) this.closeMobileLightbox();
      });
    }

    renderMobileLightboxImage() {
      const slide = this.mobileLightboxSlides[this.mobileLightboxIndex];
      if (!slide) return;
      const galleryImage = slide.querySelector('.product__modal-opener--image img');
      const mediaId = (slide.dataset.mediaId || '').split('-').pop();
      const modalImage = document.querySelector(`#ProductModal-${this.dataset.section} img[data-media-id="${mediaId}"]`);
      const image = modalImage || galleryImage;
      const srcset = image.getAttribute('srcset');
      const largestSource = srcset ? srcset.split(',').pop().trim().split(' ')[0] : image.currentSrc || image.src;
      const lightboxImage = this.mobileLightbox.querySelector('.mobile-media-lightbox__image');
      this.mobileLightbox.classList.remove('is-zoomed');
      lightboxImage.src = largestSource;
      lightboxImage.alt = galleryImage.alt || '';
      const previousButton = this.mobileLightbox.querySelector('.mobile-media-lightbox__arrow--prev');
      const nextButton = this.mobileLightbox.querySelector('.mobile-media-lightbox__arrow--next');
      previousButton.disabled = this.mobileLightboxIndex === 0;
      nextButton.disabled = this.mobileLightboxIndex === this.mobileLightboxSlides.length - 1;
    }

    changeMobileLightboxImage(direction) {
      const targetIndex = Math.min(Math.max(this.mobileLightboxIndex + direction, 0), this.mobileLightboxSlides.length - 1);
      if (targetIndex === this.mobileLightboxIndex) return;
      this.mobileLightboxIndex = targetIndex;
      this.renderMobileLightboxImage();
    }

    toggleMobileLightboxZoom() {
      this.mobileLightbox.classList.toggle('is-zoomed');
    }

    closeMobileLightbox() {
      if (!this.mobileLightbox) return;
      this.mobileLightbox.classList.remove('is-open', 'is-zoomed');
      this.mobileLightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = this.mobileLightboxPreviousOverflow || '';
    }

    setMobileGalleryHeight(activeSlide = null) {
      const slider = this.elements.viewer && this.elements.viewer.slider;
      if (!slider) return;
      if (!this.mobileMediaQuery.matches) {
        slider.style.removeProperty('height');
        return;
      }
      const currentSlide = activeSlide || slider.querySelector('.product__media-item.is-active:not(.hidden)') || slider.querySelector('.product__media-item:not(.hidden)');
      if (!currentSlide) return;
      const mediaContainer = currentSlide.querySelector('.product-media-container');
      if (!mediaContainer) return;
      const mediaWidth = mediaContainer.getBoundingClientRect().width;
      const previewRatio = parseFloat(mediaContainer.style.getPropertyValue('--preview-ratio')) || 1;
      const mediaHeight = Math.ceil(mediaWidth / previewRatio);
      if (mediaHeight > 0) {
        slider.style.height = `${mediaHeight}px`;
        slider.scrollTop = 0;
      }
    }

    setActiveMedia(mediaId, prepend, filtering = false, currentVariant = null) {
      if (filtering && currentVariant) {
        const allSlides = this.querySelectorAll('.product__media-item, .thumbnail-list__item, .slider-counter__link');
        allSlides.forEach(slide => {
          if (slide.dataset.alt === currentVariant[this.filteringOption] || slide.dataset.alt === 'always_display') {
            slide.classList.remove('hidden');
          } else {
            slide.classList.add('hidden');
          }
        })
      }

      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');
      this.setMobileGalleryHeight(activeMedia);

      if (prepend && this.prependMedia) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (filtering && currentVariant) {
        if (this.elements.viewer && this.elements.viewer.initPages) this.elements.viewer.initPages();
        if (this.elements.viewer && this.elements.viewer.update) this.elements.viewer.update();
      }

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, false, 10, true)) return;

      if (this.elements.thumbnails.vertical) {
        console.log('setActiveThumbnail')
        this.elements.thumbnails.slider.scrollTo({ top: thumbnail.offsetTop });
      } else {
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
